import _ from "lodash";
import { createUuid, Color } from "@ts3d-hoops/web-viewer";

import {
  cubeSheetId,
  cubeCameraData,
  cubeMarkup,
  cubeSelectionData,
  cubeColorData,
  cubeMagnitudeData,
  cubeFaceVisibility,
  cubeSnapshot,
  cubeLineVisibility,
  cubeCuttingData,
  cubeVisibilityData,
  cubeCurrentShadingData,
  cubeMeasurementsMarkup,
  cubeTransparencyData,
  getNodeIdsFromCubeUniqueId,
  getFileversionIdAndNodeOffsetIdMap,
  modelTransfroms,
  getFileVersionIdFromFileId,
} from "./cubeViewHelper";
import { CubeGlobal } from "@Features/viewer-core";

/**
 * The one member `toJson` reads off the viewer implementation. `HoopsViewerImpl` exposes it only
 * through its `[key: string]: any` index signature, and `ViewerContext` declares `viewerImpl`
 * nullable, so the member is proven at runtime instead of asserted.
 */
type ViewerWithClippingBox = { _clippingBox: { isActive(): boolean; toJson(): Promise<unknown> } };

function hasClippingBox(viewer: object | null): viewer is ViewerWithClippingBox {
  if (viewer === null || !("_clippingBox" in viewer)) return false;
  const box = viewer._clippingBox;
  return (
    typeof box === "object" &&
    box !== null &&
    "isActive" in box &&
    typeof box.isActive === "function" &&
    "toJson" in box &&
    typeof box.toJson === "function"
  );
}

/** A non-null object read as an open record: every member comes back as `unknown`. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** The stored `[cubeUniqueId | modelKey, payload]` pairs of a saved view. */
function isStringKeyedPair(value: unknown): value is [string, unknown] {
  return Array.isArray(value) && typeof value[0] === "string";
}

/** One entry of the stored `selection` list. */
function isSelectionEntry(value: unknown): value is { nodeId: string } {
  return typeof value === "object" && value !== null && "nodeId" in value && typeof value.nodeId === "string";
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function toArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/**
 * The saved viewer state. A flat record rather than a named shape: `toJson` writes one key per
 * sub-serialiser and `fromJson` replays them, and every consumer treats the whole thing as opaque
 * JSON (`viewpoint: unknown` in ViewsModal, `JSON.stringify` elsewhere).
 */
type CubeViewJson = Record<string, unknown>;

export const cubeView = {
  toJson: async (viewerImpl: object | null) => {
    const visibilityData = await cubeVisibilityData.toJson();

    const data: CubeViewJson = {
      camera: cubeCameraData.toJson(),
      sheetId: cubeSheetId.toJson(),
      explodeMagnitude: cubeMagnitudeData.toJson(),
      cuttingData: cubeCuttingData.toJson(),
      lineVisibility: cubeLineVisibility.toJson(),
      faceVisibility: cubeFaceVisibility.toJson(),
      defaultVisibility: visibilityData.defaultVisibility,
      visibilityExceptions: visibilityData.visibilityExceptions,
      colors: await cubeColorData.toJson(),
      markup: await cubeMarkup.toJson(),
      measurementsMarkup: await cubeMeasurementsMarkup.toJson(),
      selection: cubeSelectionData.toJson(),
      shading: cubeCurrentShadingData.toJson(),
      name: "asdf",
      uniqueId: createUuid(),
      transparencies: cubeTransparencyData.toJson(),
      modelTransfroms: modelTransfroms.toJson(),
    };

    // `viewerImpl` is `null` until the viewer mounts and `_clippingBox` is `null` until a section
    // box is created (ViewerImpl.tsx:300); the old unguarded deref threw in both cases and lost the
    // whole view, so the clipping box is now simply left out when there is none.
    if (hasClippingBox(viewerImpl) && viewerImpl._clippingBox.isActive()) {
      data.clippingBox = await viewerImpl._clippingBox.toJson();
    }

    return data;
  },
  fromJson: async (cubeViewJsonTemp: unknown, fileVersionId: string | undefined) => {
    // TODO: Discuss with purval and call getFileversionIdAndNodeOffsetIdMap globally as soon as all consitutent file loads

    // Callers hold the saved viewpoint as opaque JSON (`unknown`, or a `JSON.parse` result), so the
    // clone is walked as an open record and every field is narrowed where it is read.
    const cloned: unknown = _.cloneDeep(cubeViewJsonTemp);
    const cubeViewJson: Record<string, unknown> = isRecord(cloned) ? cloned : {};
    const mapper = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
    const colors: [number, Color][] = [];
    const visibilityExceptions: number[] = [];
    const selection: object[] = [];
    const transparencies: number[] = [];

    toArray(cubeViewJson.colors)
      .filter(isStringKeyedPair)
      .forEach((color) => {
        const fileId = color[0].split("_")[0];
        const fileVersionId = getFileVersionIdFromFileId(fileId);
        const nodeIds = getNodeIdsFromCubeUniqueId(color[0], mapper, fileVersionId);
        nodeIds.forEach((node) => {
          colors.push([node, Color.fromJson(color[1])]);
        });
      });

    cubeViewJson.colors = colors;

    toArray(cubeViewJson.visibilityExceptions)
      .filter(isString)
      .forEach((exception) => {
        const fileId = exception.split("_")[0];
        const fileVersionId = getFileVersionIdFromFileId(fileId);
        const nodeIds = getNodeIdsFromCubeUniqueId(exception, mapper, fileVersionId);
        nodeIds.forEach((node) => {
          visibilityExceptions.push(node);
        });
      });

    cubeViewJson.visibilityExceptions = visibilityExceptions;

    toArray(cubeViewJson.selection)
      .filter(isSelectionEntry)
      .forEach((selectionData) => {
        const fileId = selectionData.nodeId.split("_")[0];
        const fileVersionId = getFileVersionIdFromFileId(fileId);
        const nodeIds = getNodeIdsFromCubeUniqueId(selectionData.nodeId, mapper, fileVersionId);
        nodeIds.forEach((node) => {
          selection.push({
            ...selectionData,
            nodeId: node,
          });
        });
      });

    cubeViewJson.selection = selection;

    // `transparencySource` is the stored object itself when there is one, so rewriting `elements`
    // below keeps the original in-place mutation; `hasTransparencies` keeps the replay conditional
    // on the field having been saved at all.
    const hasTransparencies = Object.hasOwn(cubeViewJson, "transparencies");
    const transparencySource: Record<string, unknown> = isRecord(cubeViewJson.transparencies)
      ? cubeViewJson.transparencies
      : {};
    // `cubeTransparencyData.fromJson` needs a numeric opacity; the stored view always carries one.
    const transparencyValue = typeof transparencySource.value === "number" ? transparencySource.value : 0;

    toArray(transparencySource.elements)
      .filter(isString)
      .forEach((transparentElements) => {
        const fileId = transparentElements.split("_")[0];
        const fileVersionId = getFileVersionIdFromFileId(fileId);
        const nodeIds = getNodeIdsFromCubeUniqueId(transparentElements, mapper, fileVersionId);
        nodeIds.forEach((node) => {
          transparencies.push(node);
        });
      });

    transparencySource.elements = transparencies;

    await cubeMarkup.fromJson(cubeViewJson);
    if (Array.isArray(cubeViewJson.measurementsMarkup) && cubeViewJson.measurementsMarkup.length) {
      await cubeMeasurementsMarkup.fromJson(cubeViewJson.measurementsMarkup);
    }
    cubeViewJson.selection && cubeSelectionData.fromJson(cubeViewJson.selection);
    cubeViewJson.shading && cubeCurrentShadingData.fromJson(cubeViewJson.shading);
    if (hasTransparencies) {
      await cubeTransparencyData.fromJson({ elements: transparencies, value: transparencyValue });
    }
    // if(Object.hasOwn(cubeViewJson, 'clippingBox')){
    //   await viewerImpl._clippingBox.fromJson(cubeViewJson.clippingBox)
    // }
    if (cubeViewJson.modelTransfroms)
      await modelTransfroms.fromJson(toArray(cubeViewJson.modelTransfroms).filter(isStringKeyedPair));
  },
  flushView: async () => {
    await Promise.all([
      await CubeGlobal.hwv.model.resetNodesTransform(),
      CubeGlobal.hwv.measureManager.removeAllMeasurements(),
      CubeGlobal.hwv.selectionManager.clear(),
      CubeGlobal.hwv.model.resetNodesColor(),
      CubeGlobal.hwv.model.resetNodesVisibility(),
      CubeGlobal.hwv.model.resetModelOpacity(),
    ]);
  },
};

export const getCubeViewSnapshot = async () => await cubeSnapshot.toJson();
